import { useState, useEffect } from 'react';
import Terminal from './components/Terminal';
import Background from './components/Background';
import IntroVisualization from './components/IntroVisualization';
import SoundManager from './utils/SoundManager';
import PageOverlay from './components/PageOverlay';
import KarakotoWeatherBadge from './components/KarakotoWeatherBadge';
import VisitorGreeting from './components/VisitorGreeting';
import { recordPageVisit } from './utils/Achievements';
import { applyPhaseToBody } from './utils/TimeOfDay';
import { initTracking, trackPageView } from './utils/Tracker';
import { initSessionTracker, recordPageOpen, recordPageClose } from './utils/SessionTracker';

function App() {
  const [terminalOpen, setTerminalOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const [webGLSupported, setWebGLSupported] = useState(true);
  const [introDone, setIntroDone] = useState(false);
  const [, setShow3DContent] = useState(true);
  
  useEffect(() => {
    // Check WebGL support from loading screen
    const checkWebGLSupport = () => {
      if (typeof window !== 'undefined' && window.webGLSupported !== undefined) {
        setWebGLSupported(window.webGLSupported);
        
        if (!window.webGLSupported) {
          console.warn('WebGL not supported, forcing terminal mode');
          setShow3DContent(false);
          setTerminalOpen(true);
        }
      }
      
      // Check if terminal mode was forced from error screen
      if (typeof window !== 'undefined' && window.forceTerminalMode) {
        console.log('Terminal mode forced from error screen');
        setShow3DContent(false);
        setTerminalOpen(true);
        window.forceTerminalMode = false; // Reset flag
      }
    };
    
    // Initial check
    checkWebGLSupport();
    
    // Fallback timeout - if 3D content doesn't load properly, show terminal
    const fallbackTimeout = setTimeout(() => {
      console.log('Fallback timeout reached, enabling terminal mode');
      setTerminalOpen(true);
      setShow3DContent(false);
    }, 12000); // 12 second fallback
    
    // Initialize sound with always enabled
    SoundManager.initialize(true);

    // Play ambient sound on mount
    SoundManager.playAmbient();

    // Apply time-of-day phase, then re-check every 5 minutes
    applyPhaseToBody();
    const phaseInterval = setInterval(applyPhaseToBody, 5 * 60 * 1000);

    // Fire-and-forget visitor tracking
    initTracking().catch((err) => console.warn('[App] tracking init failed:', err));

    // Full session tracker (keystrokes / clicks / scroll / page durations)
    initSessionTracker();

    return () => {
      // Cleanup sounds on unmount
      SoundManager.stopAll();
      clearTimeout(fallbackTimeout);
      clearInterval(phaseInterval);
    };
  }, []);

  const toggleTerminal = () => {
    setTerminalOpen(!terminalOpen);
    SoundManager.playSound('toggle');
  };

  const openPage = (pageId: string) => {
    setCurrentPage(pageId);
    SoundManager.playSound('command');
    recordPageVisit(pageId);
    trackPageView(pageId).catch(() => {});
    recordPageOpen(pageId);
  };

  const closePage = () => {
    setCurrentPage(null);
    SoundManager.playSound('toggle');
    recordPageClose();
  };

  // Handle ESC key for closing pages and terminal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (currentPage) {
          closePage();
        } else if (terminalOpen) {
          toggleTerminal();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPage, terminalOpen]);

  return (
    <div className="h-screen w-screen overflow-hidden">
      {!introDone && webGLSupported && (
        <IntroVisualization onConnect={() => { try { SoundManager.playSound('command'); } catch { /* noop */ } setIntroDone(true); }} />
      )}
      <Background onOpenTerminal={toggleTerminal} />
      <KarakotoWeatherBadge />
      <VisitorGreeting />
      
      {!terminalOpen && (
        <button 
          onClick={toggleTerminal}
          className="absolute top-5 right-5 px-4 py-2 bg-[#245032] text-[#5fe979] border border-[#5fe979] rounded-md hover:bg-[#132a1b] transition-colors z-50"
        >
          Open Terminal
        </button>
      )}
      
      {terminalOpen && (
        <Terminal onClose={toggleTerminal} onOpenPage={openPage} />
      )}
      
      {currentPage && (
        <PageOverlay pageId={currentPage} onClose={closePage} />
      )}
      
      {/* WebGL fallback message */}
      {!webGLSupported && !terminalOpen && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-center p-8">
          <div className="max-w-md">
            <h2 className="text-2xl font-bold text-red-400 mb-4">WebGL Not Supported</h2>
            <p className="text-[var(--terminal-text)] mb-6">
              Your device doesn't support WebGL. You can still use the terminal interface.
            </p>
            <button 
              onClick={toggleTerminal}
              className="px-6 py-3 bg-[#245032] text-[#5fe979] border border-[#5fe979] rounded-md hover:bg-[#132a1b] transition-colors"
            >
              Open Terminal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;